import type { TooltipProps } from "recharts";

const fmt = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const fmtFull = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/** Renders one line per visible non-zero series plus a Total at the bottom.
 * Hidden series (passed in via `hidden`) are excluded from both the breakdown
 * lines and the Total — matching what's actually rendered in the chart so
 * the tooltip stays in sync with the legend toggle state. */
export function StackedTotalTooltip({
  active,
  payload,
  label,
  hidden,
}: TooltipProps<number, string> & { hidden?: Set<string> }) {
  if (!active || !payload || payload.length === 0) return null;
  const isHidden = (p: { dataKey?: unknown; name?: unknown }) => {
    if (!hidden) return false;
    const k = String(p.dataKey ?? p.name ?? "");
    return hidden.has(k);
  };
  const visible = payload.filter((p) => !isHidden(p) && Number(p.value) !== 0);
  const total = payload
    .filter((p) => !isHidden(p))
    .reduce((s, p) => s + Number(p.value ?? 0), 0);
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg dark:bg-slate-900 dark:border-slate-700">
      <div className="font-medium mb-1">{String(label)}</div>
      {visible.map((p) => (
        <div key={String(p.dataKey)} className="flex items-center gap-2 leading-5">
          <span
            className="inline-block h-2 w-2 rounded-sm"
            style={{ backgroundColor: p.color ?? "#94a3b8" }}
          />
          <span className="flex-1 truncate">{p.name}</span>
          <span className="tabular-nums">${fmt.format(Number(p.value ?? 0))}</span>
        </div>
      ))}
      <div className="mt-1.5 pt-1.5 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between font-medium">
        <span>Total</span>
        <span className="tabular-nums">{fmtFull.format(total)}</span>
      </div>
    </div>
  );
}
